import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Megaphone, ArrowRight, Clock, CheckCircle2, XCircle, Power } from "lucide-react";
import { toast } from "sonner";

const AD_SUB_PRICE = 4.90;
const AD_ONETIME_PRICE = 29.99;
const AD_ONETIME_DAYS = 30;

const STATUS_META = {
    pending_review: { label: "In review", cls: "text-amber-600 border-amber-300", icon: Clock },
    approved: { label: "Approved — activate to go live", cls: "text-emerald-deep border-emerald-deep", icon: CheckCircle2 },
    active: { label: "Live", cls: "text-emerald-deep border-emerald-deep", icon: Power },
    rejected: { label: "Rejected", cls: "text-red-600 border-red-300", icon: XCircle },
    inactive: { label: "Paused / ended", cls: "text-zinc-500 border-[#DDD6FE]", icon: Power },
};

const EMPTY = { business_name: "", headline: "", body: "", cta_label: "Learn more", cta_url: "", image_url: "", placement: "dashboard", billing_model: "subscription" };

export default function Advertise() {
    const [form, setForm] = useState(EMPTY);
    const [ads, setAds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [stats, setStats] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/ads/mine");
            setAds(data);
        } catch { /* ignore */ }
    };
    useEffect(() => {
        load();
        api.get("/ads/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const pick = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.business_name || !form.headline || !form.body || !form.cta_url) {
            toast.error("Please fill in business name, headline, description and link.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/ads", form);
            toast.success("Submitted for review — we'll approve it shortly.");
            setForm(EMPTY);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to submit campaign");
        } finally {
            setSubmitting(false);
        }
    };

    const activate = async (id) => {
        try {
            const { data } = await api.post(`/ads/${id}/checkout`);
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
        }
    };

    return (
        <div className="space-y-8" data-testid="advertise-root">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2">— Advertise with Flex Line</p>
                {stats && stats.active_businesses > 0 && (
                    <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-emerald-deep/10 border border-emerald-deep/20" data-testid="ads-live-counter">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-deep opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-deep"></span>
                        </span>
                        <span className="text-xs font-medium text-emerald-deep">
                            {stats.active_businesses} {stats.active_businesses === 1 ? "business is" : "businesses are"} advertising now
                        </span>
                    </div>
                )}
                <h1 className="font-display text-4xl font-light tracking-tight">
                    Put your business in front of <span className="font-serif-italic">real people.</span>
                </h1>
                <p className="text-zinc-600 mt-3 max-w-2xl">
                    Reach Flex Line members with a sponsored card on their dashboard, or list your offer in our public
                    <span className="font-medium"> Deals directory</span>. Submit your ad below — once our team approves it,
                    activate it and you go live. Renews every 3 days (${AD_SUB_PRICE.toFixed(2)} per 3 days) or one-time (${AD_ONETIME_PRICE.toFixed(2)} for {AD_ONETIME_DAYS} days).
                </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
                {/* Create form */}
                <form onSubmit={submit} className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-4" data-testid="ad-create-form">
                    <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                        <Megaphone className="w-3.5 h-3.5" /> New sponsored ad
                    </h3>
                    <Fld label="Business name" value={form.business_name} onChange={set("business_name")} testid="ad-business-name" placeholder="Acme Coffee Co." />
                    <Fld label="Headline" value={form.headline} onChange={set("headline")} testid="ad-headline" placeholder="20% off your first order" />
                    <div className="space-y-1.5">
                        <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Description</Label>
                        <Textarea value={form.body} onChange={set("body")} data-testid="ad-body-input" rows={3}
                            placeholder="Tell Flex Line members what you offer." className="rounded-sm border-[#DDD6FE] text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Fld label="Button text" value={form.cta_label} onChange={set("cta_label")} testid="ad-cta-label" placeholder="Shop now" />
                        <Fld label="Destination link" value={form.cta_url} onChange={set("cta_url")} testid="ad-cta-url" placeholder="https://…" />
                    </div>
                    <Fld label="Image URL (optional)" value={form.image_url} onChange={set("image_url")} testid="ad-image-url" placeholder="https://…/logo.png" />

                    <div className="space-y-1.5">
                        <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Placement</Label>
                        <div className="grid grid-cols-3 gap-2">
                            <Choice active={form.placement === "dashboard"} onClick={() => pick("placement", "dashboard")} testid="ad-placement-dashboard"
                                title="Dashboard" sub="Card on members' dashboard" />
                            <Choice active={form.placement === "directory"} onClick={() => pick("placement", "directory")} testid="ad-placement-directory"
                                title="Deals page" sub="Public directory at /deals" />
                            <Choice active={form.placement === "inbox"} onClick={() => pick("placement", "inbox")} testid="ad-placement-inbox"
                                title="Inbox" sub="Sponsored card in members' inbox" />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Billing</Label>
                        <div className="grid grid-cols-2 gap-2">
                            <Choice active={form.billing_model === "subscription"} onClick={() => pick("billing_model", "subscription")} testid="ad-billing-subscription"
                                title={`$${AD_SUB_PRICE.toFixed(2)} / 3 days`} sub="Auto-renews every 3 days until you cancel" />
                            <Choice active={form.billing_model === "one_time"} onClick={() => pick("billing_model", "one_time")} testid="ad-billing-onetime"
                                title={`$${AD_ONETIME_PRICE.toFixed(2)} one-time`} sub={`Runs for ${AD_ONETIME_DAYS} days`} />
                        </div>
                    </div>

                    <Button type="submit" disabled={submitting} data-testid="ad-submit-btn"
                        className="w-full bg-[#4C1D95] hover:bg-[#4C1D95]/90 text-white rounded-sm h-10">
                        {submitting ? "Submitting…" : "Submit for review"} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <p className="text-xs text-zinc-500">No charge until your ad is approved and you activate it.</p>
                </form>

                {/* Live preview */}
                <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Preview</h3>
                    <AdCard ad={{ ...form, business_name: form.business_name || "Your business", headline: form.headline || "Your headline here", body: form.body || "Your offer description appears here.", cta_label: form.cta_label || "Learn more" }} preview />
                </div>
            </div>

            {/* My campaigns */}
            <div>
                <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">My campaigns</h3>
                {ads.length === 0 ? (
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-8 text-center text-sm text-zinc-500" data-testid="ads-empty">
                        No campaigns yet — submit your first ad above.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="my-ads-list">
                        {ads.map((a) => {
                            const meta = STATUS_META[a.status] || STATUS_META.pending_review;
                            const Icon = meta.icon;
                            const price = a.billing_model === "one_time" ? `$${AD_ONETIME_PRICE.toFixed(2)} one-time` : `$${AD_SUB_PRICE.toFixed(2)} / 3 days`;
                            return (
                                <div key={a.campaign_id} className="border border-[#DDD6FE] rounded-sm bg-paper p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`my-ad-${a.campaign_id}`}>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-display text-lg">{a.headline}</span>
                                            <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${meta.cls}`}>
                                                <Icon className="w-3 h-3" /> {meta.label}
                                            </span>
                                            <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE] text-zinc-500">
                                                {a.placement === "directory" ? "Deals page" : a.placement === "inbox" ? "Inbox" : "Dashboard"}
                                            </span>
                                        </div>
                                        <p className="text-sm text-zinc-600 mt-0.5">{a.business_name} · {a.body}</p>
                                        {a.status === "active" && (
                                            <p className="font-mono text-xs text-zinc-500 mt-1" data-testid={`ad-stats-${a.campaign_id}`}>
                                                {(a.impressions || 0).toLocaleString()} views · {(a.clicks || 0).toLocaleString()} clicks
                                                {a.expires_at && ` · ends ${new Date(a.expires_at).toLocaleDateString()}`}
                                            </p>
                                        )}
                                        {a.admin_note && <p className="text-xs text-red-600 mt-1 italic">Note: {a.admin_note}</p>}
                                    </div>
                                    {(a.status === "approved" || a.status === "inactive") && (
                                        <Button onClick={() => activate(a.campaign_id)} data-testid={`activate-ad-${a.campaign_id}`}
                                            className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-sm h-9 px-4 shrink-0">
                                            Activate · {price}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function Fld({ label, value, onChange, placeholder, testid }) {
    return (
        <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            <Input value={value} onChange={onChange} placeholder={placeholder} data-testid={`${testid}-input`}
                className="rounded-sm border-[#DDD6FE] text-sm h-10" />
        </div>
    );
}

function Choice({ active, onClick, title, sub, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className={`text-left rounded-sm border p-3 transition-colors ${active ? "border-[#4C1D95] bg-[#F3E8FF]" : "border-[#DDD6FE] bg-white hover:bg-cream"}`}>
            <div className="text-sm font-medium">{title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>
        </button>
    );
}

export function AdCard({ ad, preview, onClick }) {
    if (!ad) return null;
    const fireClick = () => {
        if (ad.campaign_id) api.post(`/ads/${ad.campaign_id}/click`).catch(() => {});
        if (onClick) onClick();
    };
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5 relative overflow-hidden" data-testid={preview ? "ad-preview-card" : "dashboard-sponsored-card"}>
            <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-wider text-zinc-400 border border-[#DDD6FE] rounded-sm px-1.5 py-0.5">Sponsored</span>
            <div className="flex items-start gap-4">
                {ad.image_url ? (
                    <img src={ad.image_url} alt={ad.business_name} className="w-14 h-14 rounded-sm object-cover border border-[#DDD6FE] shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                    <div className="w-14 h-14 rounded-sm bg-[#F3E8FF] border border-[#DDD6FE] flex items-center justify-center shrink-0">
                        <Megaphone className="w-5 h-5 text-[#4C1D95]" />
                    </div>
                )}
                <div className="min-w-0 pr-16">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{ad.business_name}</p>
                    <h4 className="font-display text-lg mt-0.5">{ad.headline}</h4>
                    <p className="text-sm text-zinc-600 mt-1">{ad.body}</p>
                    {ad.cta_url && !preview ? (
                        <a href={ad.cta_url} target="_blank" rel="noopener noreferrer sponsored"
                            onClick={fireClick} data-testid="sponsored-cta-link"
                            className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[#4C1D95] link-underline">
                            {ad.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                    ) : (
                        <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[#4C1D95]">
                            {ad.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
